import { getStateName, getCourseUrl } from "../constants";
import { getLocalSeoSettings } from "../api";

const DEFAULT_BASE_URL = "https://minigolfalabama.com";

/**
 * WebSite & SearchAction Schema
 */
export function buildWebsiteSchema(customSettings) {
  const settings = customSettings || getLocalSeoSettings();
  if (settings.enableWebsiteSchema === false) return null;
  const baseUrl = settings.siteUrl || DEFAULT_BASE_URL;
  const siteName = settings.siteName || "Mini Golf USA";

  return {
    "@type": "WebSite",
    name: siteName,
    alternateName: ["Mini Golf Alabama", "Mini Golf Near Me", "Putt Putt Directory"],
    url: baseUrl,
    description:
      settings.defaultDescription ||
      "Find the best indoor and outdoor mini golf, putt putt, and glow golf courses in Alabama and across the USA.",
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${baseUrl}/state/alabama?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

/**
 * Organization Schema
 */
export function buildOrganizationSchema(customSettings) {
  const settings = customSettings || getLocalSeoSettings();
  if (settings.enableOrganizationSchema === false) return null;
  const baseUrl = settings.siteUrl || DEFAULT_BASE_URL;
  const siteName = settings.siteName || "Mini Golf USA";

  const socialLinks = [
    settings.socialFacebook,
    settings.socialTwitter,
    settings.socialInstagram,
    settings.socialYoutube,
  ].filter(Boolean);

  return {
    "@type": "Organization",
    name: siteName,
    url: baseUrl,
    logo: settings.logoUrl || `${baseUrl}/logo.png`,
    sameAs: socialLinks.length > 0 ? socialLinks : [
      "https://facebook.com",
      "https://twitter.com",
      "https://instagram.com",
    ],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "Customer Support",
      email: settings.contactEmail || "contact@minigolfalabama.com",
      telephone: settings.contactPhone || undefined,
      availableLanguage: ["English"],
    },
  };
}

/**
 * LocalBusiness / SportsActivityLocation Schema for a Course
 */
export function buildCourseSchema(course, stateObj, currentUrl) {
  if (!course) return null;

  // 1. Check if course has raw custom JSON-LD
  if (course.customJsonLd && typeof course.customJsonLd === "string" && course.customJsonLd.trim().startsWith("{")) {
    try {
      const parsed = JSON.parse(course.customJsonLd);
      if (parsed) return parsed;
    } catch (e) {}
  }

  const settings = getLocalSeoSettings();
  if (settings.enableCourseSchema === false) return null;
  const baseUrl = settings.siteUrl || DEFAULT_BASE_URL;

  const stateCode = (course.state || stateObj?.code || "al").toUpperCase();
  const stateName = getStateName(stateCode) || "Alabama";
  const desc = course.seoDescription || (course.description && !course.description.startsWith("http")
    ? course.description
    : `Find ${course.name}, an exciting mini golf course located in ${course.city}, ${stateName}. Rated ${course.rating || 4.5} stars from ${course.reviewCount || 0} reviews.`);

  const openingHours = course.hours
    ? Object.entries(course.hours).map(([day, hrs]) => ({
        "@type": "OpeningHoursSpecification",
        dayOfWeek: `https://schema.org/${day}`,
        description: hrs,
      }))
    : [];

  const primaryType = course.customSchemaType || "SportsActivityLocation";
  const typeArray = Array.isArray(primaryType)
    ? primaryType
    : primaryType === "SportsActivityLocation"
    ? ["SportsActivityLocation", "AmusementPark", "LocalBusiness"]
    : [primaryType, "LocalBusiness"];

  return {
    "@type": typeArray,
    name: course.name,
    image: course.image || `${baseUrl}/favicon.png`,
    description: desc,
    telephone: course.phone || undefined,
    url: course.website && course.website !== "https://example.com" ? course.website : currentUrl,
    priceRange: course.priceRange || "$$",
    address: {
      "@type": "PostalAddress",
      streetAddress: course.address || undefined,
      addressLocality: course.city,
      addressRegion: stateCode,
      addressCountry: "US",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: parseFloat(course.rating) || 4.5,
      reviewCount: parseInt(course.reviewCount, 10) || 10,
      bestRating: 5,
      worstRating: 1,
    },
    openingHoursSpecification: openingHours.length > 0 ? openingHours : undefined,
  };
}

/**
 * FAQPage Schema
 */
export function buildFaqSchema(faqs = []) {
  if (!faqs || faqs.length === 0) return null;
  const settings = getLocalSeoSettings();
  if (settings.enableFaqSchema === false) return null;

  return {
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.a,
      },
    })),
  };
}

/**
 * BreadcrumbList Schema
 */
export function buildBreadcrumbSchema(items = []) {
  if (!items || items.length === 0) return null;
  const settings = getLocalSeoSettings();
  if (settings.enableBreadcrumbSchema === false) return null;
  const baseUrl = settings.siteUrl || DEFAULT_BASE_URL;

  return {
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url.startsWith("http") ? item.url : `${baseUrl}${item.url}`,
    })),
  };
}

/**
 * CollectionPage & ItemList Schema for State and City Course Listings
 */
export function buildCollectionSchema(title, description, courses = [], currentUrl) {
  const settings = getLocalSeoSettings();
  if (settings.enableCollectionSchema === false) return null;
  const baseUrl = settings.siteUrl || DEFAULT_BASE_URL;

  return {
    "@type": "CollectionPage",
    name: title,
    description: description,
    url: currentUrl || baseUrl,
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: courses.length,
      itemListElement: courses.slice(0, 30).map((c, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: c.name,
        url: `${baseUrl}${getCourseUrl(c)}`,
      })),
    },
  };
}

/**
 * BlogPosting / Article Schema
 */
export function buildArticleSchema(post, currentUrl) {
  if (!post) return null;
  const settings = getLocalSeoSettings();
  if (settings.enableBlogSchema === false) return null;
  const baseUrl = settings.siteUrl || DEFAULT_BASE_URL;
  const siteName = settings.siteName || "Mini Golf USA";

  return {
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt || post.content?.slice(0, 160) || post.title,
    image: post.image || `${baseUrl}/favicon.png`,
    datePublished: post.createdAt || new Date().toISOString(),
    dateModified: post.updatedAt || post.createdAt || new Date().toISOString(),
    author: {
      "@type": "Person",
      name: post.author || "Mini Golf Editorial Team",
    },
    publisher: {
      "@type": "Organization",
      name: siteName,
      logo: {
        "@type": "ImageObject",
        url: settings.logoUrl || `${baseUrl}/logo.png`,
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": currentUrl || baseUrl,
    },
  };
}
